import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import Pagination from '../../components/common/Pagination';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';


interface Subscription {
  id: string;
  tenant: {
    id: string;
    name: string;
    subdomain: string;
  };
  plan: {
    name: string;
    slug: string;
    price_monthly?: number;
    price_annual?: number;
  };
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | 'paused';
  billing_cycle: 'monthly' | 'annual';
  billing_type: 'automatic' | 'manual';
  current_period_start: string;
  current_period_end: string;
  trial_ends_at: string | null;
  created_at: string;
}

interface Plan {
  id: string;
  name: string;
  slug: string;
  price_monthly: number;
  price_annual: number;
}

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  is_active: boolean;
  currency?: 'USD' | 'BRL';
  country?: string;
}

export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [changePlanId, setChangePlanId] = useState('');
  const [createForm, setCreateForm] = useState({
    tenant_id: '',
    plan_id: '',
    billing_cycle: 'monthly' as 'monthly' | 'annual',
    currency: 'USD' as 'USD' | 'BRL',
    contractStartDate: new Date().toISOString().split('T')[0],
    contractEndDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    purchaseOrderNumber: '',
    billingContactEmail: '',
    billingContactName: '',
    notes: '',
  });

  useEffect(() => {
    loadSubscriptions();
    loadPlans();
    loadTenants();
  }, []);

  const loadSubscriptions = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/v1/billing/admin/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setSubscriptions(data.data.subscriptions || []);
      }
    } catch (error) {
      console.error('Failed to load subscriptions', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/billing/plans`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setPlans(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load plans', error);
    }
  };

  const loadTenants = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setTenants(data.data.tenants || []);
      }
    } catch (error) {
      console.error('Failed to load tenants', error);
    }
  };

  const handleCreateSubscription = async () => {
    if (!createForm.tenant_id || !createForm.plan_id || !createForm.contractStartDate || !createForm.contractEndDate) {
      toast.error('Please fill in all required fields (Tenant ID, Plan, Contract Start Date, and Contract End Date)', {
        duration: 4000,
        icon: '⚠️',
      });
      return;
    }

    try {
      // Convert snake_case to camelCase for backend
      const payload = {
        tenantId: createForm.tenant_id,
        planId: createForm.plan_id,
        billingCycle: createForm.billing_cycle,
        currency: createForm.currency,
        contractStartDate: createForm.contractStartDate,
        contractEndDate: createForm.contractEndDate,
        purchaseOrderNumber: createForm.purchaseOrderNumber || undefined,
        billingContactEmail: createForm.billingContactEmail || undefined,
        billingContactName: createForm.billingContactName || undefined,
        notes: createForm.notes || undefined,
      };

      const response = await fetch(`${API_BASE_URL}/v1/billing/admin/subscriptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Subscription created successfully!', {
          duration: 3000,
          icon: '✅',
        });
        setShowCreateModal(false);
        setTenantSearch('');
        setCreateForm({
          tenant_id: '',
          plan_id: '',
          billing_cycle: 'monthly',
          currency: 'USD',
          contractStartDate: new Date().toISOString().split('T')[0],
          contractEndDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          purchaseOrderNumber: '',
          billingContactEmail: '',
          billingContactName: '',
          notes: '',
        });
        loadSubscriptions();
      } else {
        toast.error(data.error || 'Failed to create subscription', {
          duration: 5000,
          icon: '❌',
        });
      }
    } catch (error) {
      console.error('Failed to create subscription', error);
      toast.error('Error creating subscription', {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleCancelSubscription = async (subscription: Subscription) => {
    if (!confirm(`Cancel subscription for "${subscription.tenant.name}"? This will cancel it immediately.`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/v1/billing/admin/subscriptions/${subscription.id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ immediately: true }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Subscription canceled successfully');
        loadSubscriptions();
      } else {
        toast.error(data.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Failed to cancel subscription', error);
      toast.error('Error canceling subscription');
    }
  };

  const handleChangePlan = async () => {
    if (!selectedSubscription || !changePlanId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/v1/billing/admin/subscriptions/${selectedSubscription.id}/change-plan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newPlanId: changePlanId }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Plan changed successfully');
        setShowChangePlanModal(false);
        setSelectedSubscription(null);
        setChangePlanId('');
        loadSubscriptions();
      } else {
        toast.error(data.error || 'Failed to change plan');
      }
    } catch (error) {
      console.error('Failed to change plan', error);
      toast.error('Error changing plan');
    }
  };

  const handleDeleteSubscription = async () => {
    if (!selectedSubscription) return;

    try {
      const response = await fetch(`${API_BASE_URL}/v1/billing/admin/subscriptions/${selectedSubscription.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Subscription deleted successfully');
        setShowDeleteConfirm(false);
        setSelectedSubscription(null);
        loadSubscriptions();
      } else {
        toast.error(data.error || 'Failed to delete subscription');
      }
    } catch (error) {
      console.error('Failed to delete subscription', error);
      toast.error('Error deleting subscription');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      canceled: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      past_due: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    };

    const statusLower = status?.toLowerCase();
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[statusLower as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  const getBillingTypeBadge = (type: string) => {
    const colors = {
      automatic: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
      manual: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    };

    const typeLower = type?.toLowerCase();
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[typeLower as keyof typeof colors]}`}>
        {typeLower === 'automatic' ? '💳 Stripe' : '📄 Manual'}
      </span>
    );
  };

  // Calculate MRR and ARR
  const calculateRevenue = () => {
    let mrr = 0;
    let arr = 0;

    subscriptions
      .filter(s => s.status?.toLowerCase() === 'active' || s.status?.toLowerCase() === 'trialing')
      .forEach(sub => {
        if (sub.billing_cycle?.toLowerCase() === 'monthly') {
          const monthlyPrice = Number(sub.plan.price_monthly || 0);
          mrr += monthlyPrice;
          arr += monthlyPrice * 12;
        } else if (sub.billing_cycle?.toLowerCase() === 'annual') {
          const annualPrice = Number(sub.plan.price_annual || 0);
          mrr += annualPrice / 12;
          arr += annualPrice;
        }
      });

    return { mrr, arr };
  };

  const { mrr, arr } = calculateRevenue();

  // Paginate results
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubscriptions = subscriptions.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  return (
    <MainLayout>
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Subscriptions</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            View and manage all customer subscriptions
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
        >
          + Create Manual Subscription
        </button>
      </div>

      {/* Statistics */}
      <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-6">
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">MRR</div>
          <div className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
            ${mrr.toFixed(0)}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Monthly Recurring</div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">ARR</div>
          <div className="mt-2 text-3xl font-bold text-indigo-600 dark:text-indigo-400">
            ${arr.toFixed(0)}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Annual Recurring</div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Active</div>
          <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {subscriptions.filter(s => s.status?.toLowerCase() === 'active').length}
          </div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Trialing</div>
          <div className="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">
            {subscriptions.filter(s => s.status?.toLowerCase() === 'trialing').length}
          </div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Manual Billing</div>
          <div className="mt-2 text-3xl font-bold text-purple-600 dark:text-purple-400">
            {subscriptions.filter(s => s.billing_type?.toLowerCase() === 'manual').length}
          </div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Past Due</div>
          <div className="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">
            {subscriptions.filter(s => s.status?.toLowerCase() === 'past_due').length}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Current Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                    </div>
                  </td>
                </tr>
              ) : subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No subscriptions found
                  </td>
                </tr>
              ) : (
                paginatedSubscriptions.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex flex-col">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {subscription.tenant.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {subscription.tenant.subdomain}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {subscription.plan.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {subscription.billing_cycle}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {getStatusBadge(subscription.status)}
                      {subscription.trial_ends_at && subscription.status?.toLowerCase() === 'trialing' && (
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Trial ends {new Date(subscription.trial_ends_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {getBillingTypeBadge(subscription.billing_type)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      <div>{new Date(subscription.current_period_start).toLocaleDateString()}</div>
                      <div className="text-xs">to {new Date(subscription.current_period_end).toLocaleDateString()}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(subscription.created_at).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setChangePlanId('');
                            setShowChangePlanModal(true);
                          }}
                          className="rounded bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
                          title="Change Plan"
                        >
                          Change Plan
                        </button>
                        {subscription.status?.toLowerCase() !== 'canceled' && (
                          <button
                            onClick={() => handleCancelSubscription(subscription)}
                            className="rounded bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:hover:bg-yellow-900/50"
                            title="Cancel Subscription"
                          >
                            Cancel
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setShowDeleteConfirm(true);
                          }}
                          className="rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                          title="Delete Subscription"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {subscriptions.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={subscriptions.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </div>

      {/* Create Manual Subscription Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="max-w-2xl w-full my-8 rounded-lg bg-white p-6 dark:bg-gray-800">
            <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
              Create Manual Subscription
            </h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              For enterprise customers with custom contracts or manual payment arrangements.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer Tenant *
                </label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tenantSearch}
                      onChange={(e) => setTenantSearch(e.target.value)}
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="🔍 Search by name or subdomain..."
                    />
                    {tenantSearch && (
                      <button
                        onClick={() => setTenantSearch('')}
                        className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <select
                    value={createForm.tenant_id}
                    onChange={(e) => setCreateForm({ ...createForm, tenant_id: e.target.value })}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    size={6}
                  >
                    <option value="">-- Select a tenant --</option>
                    {tenants
                      .filter(t => {
                        if (!tenantSearch) return true;
                        const search = tenantSearch.toLowerCase();
                        return t.name.toLowerCase().includes(search) ||
                               t.subdomain.toLowerCase().includes(search);
                      })
                      .sort((a, b) => {
                        // Sort: active first, then alphabetically by name
                        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
                        return a.name.localeCompare(b.name);
                      })
                      .map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name} ({tenant.subdomain}) {!tenant.is_active ? '⚠️ INACTIVE' : '✓'}
                        </option>
                      ))}
                  </select>
                  {createForm.tenant_id && (
                    <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Selected: {tenants.find(t => t.id === createForm.tenant_id)?.name || 'Unknown'}</span>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {tenants.filter(t => {
                    if (!tenantSearch) return true;
                    const search = tenantSearch.toLowerCase();
                    return t.name.toLowerCase().includes(search) || t.subdomain.toLowerCase().includes(search);
                  }).length} of {tenants.length} tenants shown
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plan *
                </label>
                <select
                  value={createForm.plan_id}
                  onChange={(e) => setCreateForm({ ...createForm, plan_id: e.target.value })}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a plan</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - ${plan.price_monthly}/mo or ${plan.price_annual}/yr
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Billing Cycle *
                  </label>
                  <select
                    value={createForm.billing_cycle}
                    onChange={(e) => setCreateForm({ ...createForm, billing_cycle: e.target.value as 'monthly' | 'annual' })}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Currency *
                  </label>
                  <select
                    value={createForm.currency}
                    onChange={(e) => setCreateForm({ ...createForm, currency: e.target.value as 'USD' | 'BRL' })}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="BRL">BRL (R$)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Currency is set once and cannot be changed after Stripe customer is created
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contract Start Date *
                  </label>
                  <input
                    type="date"
                    value={createForm.contractStartDate}
                    onChange={(e) => setCreateForm({ ...createForm, contractStartDate: e.target.value })}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contract End Date *
                  </label>
                  <input
                    type="date"
                    value={createForm.contractEndDate}
                    onChange={(e) => setCreateForm({ ...createForm, contractEndDate: e.target.value })}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Purchase Order Number
                </label>
                <input
                  type="text"
                  value={createForm.purchaseOrderNumber}
                  onChange={(e) => setCreateForm({ ...createForm, purchaseOrderNumber: e.target.value })}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder="PO-12345 (optional)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Billing Contact Name
                  </label>
                  <input
                    type="text"
                    value={createForm.billingContactName}
                    onChange={(e) => setCreateForm({ ...createForm, billingContactName: e.target.value })}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder="John Doe (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Billing Contact Email
                  </label>
                  <input
                    type="email"
                    value={createForm.billingContactEmail}
                    onChange={(e) => setCreateForm({ ...createForm, billingContactEmail: e.target.value })}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder="billing@company.com (optional)"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder="Any additional notes about this contract... (optional)"
                  rows={3}
                />
              </div>

              <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                <div className="flex">
                  <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Note:</strong> Manual subscriptions are for customers who pay via invoice, bank transfer, or have custom agreements.
                      They won't be charged automatically through Stripe.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setTenantSearch('');
                  setCreateForm({
          tenant_id: '',
          plan_id: '',
          billing_cycle: 'monthly',
          currency: 'USD',
          contractStartDate: new Date().toISOString().split('T')[0],
          contractEndDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          purchaseOrderNumber: '',
          billingContactEmail: '',
          billingContactName: '',
          notes: '',
        });
                }}
                className="rounded-md px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSubscription}
                className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
              >
                Create Subscription
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Change Plan Modal */}
      {showChangePlanModal && selectedSubscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-w-md w-full rounded-lg bg-white p-6 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
              Change Plan
            </h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Changing plan for <strong>{selectedSubscription.tenant.name}</strong>.
              Current plan: <strong>{selectedSubscription.plan.name}</strong>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Plan
              </label>
              <select
                value={changePlanId}
                onChange={(e) => setChangePlanId(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select a plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ${plan.price_monthly}/mo or ${plan.price_annual}/yr
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowChangePlanModal(false);
                  setSelectedSubscription(null);
                  setChangePlanId('');
                }}
                className="rounded-md px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePlan}
                disabled={!changePlanId}
                className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Change Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedSubscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-w-md w-full rounded-lg bg-white p-6 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-bold text-red-600 dark:text-red-400">
              Delete Subscription
            </h3>
            <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to permanently delete the subscription for
              <strong> {selectedSubscription.tenant.name}</strong>?
            </p>
            <p className="mb-4 text-sm text-red-600 dark:text-red-400">
              This action cannot be undone. If this is a Stripe subscription, it will also be canceled in Stripe.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedSubscription(null);
                }}
                className="rounded-md px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Keep
              </button>
              <button
                onClick={handleDeleteSubscription}
                className="rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </MainLayout>
  );
}
